"use client";

import { useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, ToasterProps } from "sonner";

const Toaster = ({ richColors: _richColors, ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const seenToastsRef = useRef<WeakSet<Element>>(new WeakSet());

  useEffect(() => {
    let audioContext: AudioContext | null = null;

    const playPopupSound = () => {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) return;
        audioContext = audioContext ?? new AudioContextClass();
        if (audioContext.state === "suspended") {
          void audioContext.resume();
        }

        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        const now = audioContext.currentTime;

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(740, now);
        oscillator.frequency.exponentialRampToValueAtTime(980, now + 0.08);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.045, now + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);

        oscillator.connect(gain);
        gain.connect(audioContext.destination);
        oscillator.start(now);
        oscillator.stop(now + 0.17);
      } catch {
        // Audio may be blocked by browser policy before user interaction.
      }
    };

    const handleNode = (node: Node) => {
      if (!(node instanceof Element)) return;
      const toast = node.matches("[data-sonner-toast]")
        ? node
        : node.querySelector("[data-sonner-toast]");
      if (!toast || seenToastsRef.current.has(toast)) return;
      seenToastsRef.current.add(toast);
      playPopupSound();
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(handleNode);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      void audioContext?.close();
    };
  }, []);

  return (
    <Sonner
      theme="dark"
      className="toaster group"
      visibleToasts={3}
      richColors={false}
      style={
        {
          // Background colors - deep black
          "--normal-bg": "#000000",
          "--normal-text": "#ffffff",
          "--normal-border": "#050505",
          
          // Success state
          "--success-bg": "#000000",
          "--success-text": "#ffffff",
          "--success-border": "#050505",
          
          // Error state
          "--error-bg": "#000000",
          "--error-text": "#ffffff",
          "--error-border": "#050505",
          
          // Warning state
          "--warning-bg": "#000000",
          "--warning-text": "#ffffff",
          "--warning-border": "#050505",
          
          // Info state
          "--info-bg": "#000000",
          "--info-text": "#ffffff",
          "--info-border": "#050505",
        } as React.CSSProperties
      }
      {...props}
      toastOptions={{
        style: {
          background: "#000000",
          color: "#ffffff",
          border: "1px solid #050505",
          borderRadius: "22px",
          padding: "30px 40px",
          width: "min(680px, calc(100vw - 32px))",
          minHeight: "112px",
          fontSize: "20px",
          fontWeight: "700",
          letterSpacing: "0",
          boxShadow: "0 30px 90px rgba(0, 0, 0, 0.72), 0 14px 38px rgba(0, 0, 0, 0.52)",
          backdropFilter: "blur(10px)",
        },
        success: {
          style: {
            background: "#000000",
            color: "#ffffff",
            border: "1px solid #050505",
          },
          iconTheme: {
            primary: "#ffffff",
            secondary: "#000000",
          },
        },
        error: {
          style: {
            background: "#000000",
            color: "#ffffff",
            border: "1px solid #050505",
          },
          iconTheme: {
            primary: "#ef4444",
            secondary: "#000000",
          },
        },
        warning: {
          style: {
            background: "#000000",
            color: "#ffffff",
            border: "1px solid #050505",
          },
          iconTheme: {
            primary: "#f59e0b",
            secondary: "#000000",
          },
        },
        info: {
          style: {
            background: "#000000",
            color: "#ffffff",
            border: "1px solid #050505",
          },
          iconTheme: {
            primary: "#3b82f6",
            secondary: "#000000",
          },
        },
      }}
    />
  );
};

export { Toaster };
